import * as React from 'react';
import Modal from 'react-modal';
import { ApolloError } from '@apollo/client';

import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import BoxBody from 'components/BoxBody';
import ConfirmDialog from 'components/ConfirmDialog';
import EventTracking from 'lib/EventTracking';
import SelectFromList from './SelectFromList';
import TargetByStudentId from './TargetByStudentId';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import { ComposerContext } from './ComposerContext';

import { MessageType } from './SplitComposer';

import '../SplitComposer.scss';
import { Recipient } from 'types/typescript/messagingTypes';
import { Backbone$Id } from 'types/typescript/Backbone';
import { StudentIdListRecipient, ComposerOrganization } from 'types/typescript/messagingTypes';

export type SelectRecipientTabs = 'select_from_list' | 'target_by_student_id';
export type TargetByStudentIdPages = 'upload_list_null' | 'upload_list' | 'list_of_uploaded_users';

type Props = {
  defaultTab?: SelectRecipientTabs;
  onContinue: () => void;
  onTabChange: () => void;
  onSuccessfulStudentIdListCreation: () => void;
  groupsError?: ApolloError;
  orgsError?: ApolloError;
  loadingGroupResults: boolean;
  loadingPeopleResults: boolean;
  organizations?: ComposerOrganization[];
  selection: Recipient[];
  initialTargetId?: Backbone$Id;
  messageType: MessageType;
};

type State = {
  confirmingSwitchToTab: SelectRecipientTabs | null;
  activeTab: SelectRecipientTabs;
  targetByStudentIdPage: TargetByStudentIdPages | null;
};

class SelectRecipientPanel extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const studentIdListRecipientInSelection = props.selection.find(
      (s) => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );
    const shouldSeeTabs =
      props.organizations &&
      props.organizations.some((organization) => organization.permissions.canSendToStudentIdList);

    const defaultTab = studentIdListRecipientInSelection
      ? 'target_by_student_id'
      : props.defaultTab || 'select_from_list';

    this.state = {
      confirmingSwitchToTab: null,
      activeTab: shouldSeeTabs ? defaultTab : 'select_from_list',
      targetByStudentIdPage: null,
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevState.targetByStudentIdPage !== this.state.targetByStudentIdPage &&
      this.state.targetByStudentIdPage === 'list_of_uploaded_users'
    ) {
      this.props.onSuccessfulStudentIdListCreation();
    }

    const previouslySawTabs =
      prevProps.organizations &&
      prevProps.organizations.some(
        (organization) => organization.permissions.canSendToStudentIdList
      );
    const shouldNowSeeTabs =
      this.props.organizations &&
      this.props.organizations.some(
        (organization) => organization.permissions.canSendToStudentIdList
      );
    if (!previouslySawTabs && shouldNowSeeTabs && this.props.defaultTab) {
      this.switchToTab(this.props.defaultTab);
    }
  }

  switchToTab = (tab: SelectRecipientTabs) => {
    this.setState({ activeTab: tab });
  };

  setTargetByStudentIdPage = (page: TargetByStudentIdPages) => {
    this.setState({ targetByStudentIdPage: page });
  };

  renderSelectFromList() {
    const {
      onContinue,
      groupsError,
      loadingGroupResults,
      loadingPeopleResults,
      messageType,
      orgsError,
    } = this.props;

    return (
      <ComposerContext.Consumer>
        {(composerContext) => (
          <SelectFromList
            onContinue={onContinue}
            groupsError={groupsError}
            orgsError={orgsError}
            loadingGroupResults={loadingGroupResults}
            loadingPeopleResults={loadingPeopleResults}
            messageType={messageType}
            isUrgentMessage={composerContext.isUrgentMessage}
            uiEventContext={composerContext.getUIEventContext()}
          />
        )}
      </ComposerContext.Consumer>
    );
  }

  render() {
    const { onContinue, selection, organizations, initialTargetId, messageType } = this.props;
    const { activeTab, confirmingSwitchToTab, targetByStudentIdPage } = this.state;

    const shouldSeeTabs =
      organizations &&
      organizations.some((organization) => organization.permissions.canSendToStudentIdList) &&
      messageType !== 'urgent'; // don't show target by student id for urgent messsages during pilot.

    const studentIdListRecipientInSelection = selection.find(
      (s) => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );

    const tabs: SelectRecipientTabs[] = ['select_from_list', 'target_by_student_id'];

    const userHasManuallySelectedRecipients = initialTargetId
      ? selection.some((item) => item.id !== initialTargetId)
      : selection.length;

    return (
      <React.Fragment>
        <Modal isOpen={!!confirmingSwitchToTab} data-test="switch-tab-modal">
          <ConfirmDialog
            title="You're about to switch to a different way of selecting recipients."
            onConfirm={() => {
              this.setState({ activeTab: confirmingSwitchToTab || activeTab });
              this.props.onTabChange();
            }}
            actionButtonText="Continue"
            confirmEventKey={`announcements.${activeTab}.switch_tabs_dialog.continue.click`}
            cancelEventKey={`announcements.${activeTab}.switch_tabs_dialog.cancel.click`}
            cancelButtonText="Cancel"
            onRequestHide={() => this.setState({ confirmingSwitchToTab: null })}
          >
            <Text color="graphite">The recipients you’ve already selected will be removed.</Text>
          </ConfirmDialog>
        </Modal>
        <div className="SelectRecipientPanel">
          <div className="SelectRecipientPanel-inner">
            {shouldSeeTabs ? (
              <Tabs className="SelectRecipientPanel-inner-wrap" tabActive={tabs.indexOf(activeTab)}>
                <TabPanel
                  title={
                    <Text size="xxxsmall" smallCaps>
                      Choose groups or people
                    </Text>
                  }
                  className="BoxBody BoxBody--full SelectRecipientPanel-inner-body"
                  disableActivationOnClick
                  onClick={() => {
                    if (targetByStudentIdPage) {
                      EventTracking.trackKey(
                        `announcements.target_by_student_id.${targetByStudentIdPage}.choose_from_list_tab.click`
                      );
                    }
                    if (
                      studentIdListRecipientInSelection ||
                      targetByStudentIdPage === 'list_of_uploaded_users'
                    ) {
                      this.setState({ confirmingSwitchToTab: tabs[0] });
                      return;
                    }
                    if (activeTab !== tabs[0]) {
                      this.setState({ activeTab: tabs[0] });
                      this.props.onTabChange();
                    }
                  }}
                >
                  {this.renderSelectFromList()}
                </TabPanel>
                <TabPanel
                  dataTestSelector="target-by-student-id"
                  title={
                    <Text size="xxxsmall" smallCaps>
                      Upload list of student numbers
                    </Text>
                  }
                  className="BoxBody BoxBody--full SelectRecipientPanel-inner-body"
                  disableActivationOnClick
                  onClick={() => {
                    if (userHasManuallySelectedRecipients) {
                      this.setState({ confirmingSwitchToTab: tabs[1] });
                      return;
                    }
                    if (activeTab !== tabs[1]) {
                      this.setState({ activeTab: tabs[1] });
                      this.props.onTabChange();
                    }
                  }}
                >
                  <TargetByStudentId
                    organizations={
                      organizations &&
                      organizations.filter(
                        (organization) => organization.permissions.canSendToStudentIdList
                      )
                    }
                    onContinue={onContinue}
                    studentIdListRecipientInSelection={
                      studentIdListRecipientInSelection as StudentIdListRecipient
                    }
                    onPageChange={this.setTargetByStudentIdPage}
                  />
                </TabPanel>
              </Tabs>
            ) : (
              <BoxBody full divided className="SelectRecipientPanel-inner-body">
                {this.renderSelectFromList()}
              </BoxBody>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default SelectRecipientPanel;
